/** Port Consumption Database — consumption difference helpers. */

export type PortDatabaseOpKey = 'maneuvering' | 'cargoOps' | 'idle';

export interface ConsumptionDifferenceView {
  differenceText: string;
  differenceClass: 'positive' | 'negative' | 'neutral' | 'unavailable';
  performanceLabel: string;
}

const OP_LABELS: Record<PortDatabaseOpKey, string> = {
  maneuvering: 'Manoeuvring',
  cargoOps: 'Cargo Ops',
  idle: 'Idle',
};

const OP_FIELD_MAP: Record<
  PortDatabaseOpKey,
  { actual: string; warranted: string; design: string }
> = {
  maneuvering: {
    actual: 'maneuvering_actual_consumption_mt',
    warranted: 'maneuvering_warranted_consumption_mt',
    design: 'maneuvering_design_consumption_mt',
  },
  cargoOps: {
    actual: 'cargo_ops_actual_consumption_mt',
    warranted: 'cargo_ops_warranted_consumption_mt',
    design: 'cargo_ops_design_consumption_mt',
  },
  idle: {
    actual: 'idle_actual_consumption_mt',
    warranted: 'idle_warranted_consumption_mt',
    design: 'idle_design_consumption_mt',
  },
};

export function toFiniteMetric(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatTooltipMetric(value: number | null): string {
  const metric = toFiniteMetric(value);
  if (metric === null) {
    return '--';
  }
  return `${metric.toFixed(2)} MT`;
}

/** Comparison minus actual; null when either operand is missing.
 * Positive = actual below comparison (savings). Negative = overconsumption.
 */
export function calculateConsumptionDifference(
  actual: number | null,
  comparison: number | null,
): number | null {
  if (actual === null || comparison === null) {
    return null;
  }
  return comparison - actual;
}

export function formatSignedDifference(value: number | null | undefined): string {
  const metric = toFiniteMetric(value);
  if (metric === null) {
    return '--';
  }
  if (metric === 0) {
    return '0.00 MT';
  }
  const sign = metric > 0 ? '+' : '-';
  return `${sign}${Math.abs(metric).toFixed(2)} MT`;
}

/** positive (+) = green/better, negative (-) = red/worse. */
export function getDifferenceClass(
  value: number | null,
): 'positive' | 'negative' | 'neutral' | 'unavailable' {
  if (value === null) {
    return 'unavailable';
  }
  if (value > 0) {
    return 'positive';
  }
  if (value < 0) {
    return 'negative';
  }
  return 'neutral';
}

export function getPerformanceLabel(value: number | null): string {
  if (value === null) {
    return '--';
  }
  if (value > 0) {
    return 'Better';
  }
  if (value < 0) {
    return 'Worse';
  }
  return 'Neutral';
}

export function getOpActualValue(element: any, op: PortDatabaseOpKey): number | null {
  const field = OP_FIELD_MAP[op].actual;
  return toFiniteMetric(
    element?.actual_consumptions_mt_maneuvering_cargo_idle?.[field] ??
    element?.[field],
  );
}

export function getOpWarrantedValue(element: any, op: PortDatabaseOpKey): number | null {
  const field = OP_FIELD_MAP[op].warranted;
  return toFiniteMetric(
    element?.warranted_consumptions_mt_maneuvering_cargo_idle?.[field] ??
    element?.[field],
  );
}

export function getOpDesignValue(element: any, op: PortDatabaseOpKey): number | null {
  const field = OP_FIELD_MAP[op].design;
  return toFiniteMetric(
    element?.design_consumptions_mt_maneuvering_cargo_idle?.[field] ??
    element?.[field],
  );
}

function isNonEmptyOpMetric(val: unknown): boolean {
  if (val === null || val === undefined || val === '') {
    return false;
  }
  if (typeof val === 'string') {
    const trimmed = val.trim().toLowerCase();
    if (
      trimmed === '' ||
      trimmed === '--' ||
      trimmed === '-' ||
      trimmed === 'none' ||
      trimmed === 'nan' ||
      trimmed === 'null'
    ) {
      return false;
    }
  }
  const parsed = Number(typeof val === 'string' ? val.replace(/,/g, '') : val);
  return Number.isFinite(parsed) && parsed !== 0;
}

/**
 * Checks whether an element has valid actual consumption data for any of
 * the three operation legs: Maneuvering, Cargo Ops, or Idle.
 * Returns true if at least one leg has numeric data (non-null, non-empty, non-zero).
 * Returns false if all three legs have no data (empty, missing, null, undefined, '--', or 0).
 */
export function hasAnyActualOpData(element: any): boolean {
  if (!element || typeof element !== 'object') {
    return false;
  }
  const actualObj = element?.actual_consumptions_mt_maneuvering_cargo_idle;
  const man = actualObj?.maneuvering_actual_consumption_mt ?? element?.maneuvering_actual_consumption_mt;
  const cargo = actualObj?.cargo_ops_actual_consumption_mt ?? element?.cargo_ops_actual_consumption_mt;
  const idle = actualObj?.idle_actual_consumption_mt ?? element?.idle_actual_consumption_mt;

  return (
    isNonEmptyOpMetric(man) ||
    isNonEmptyOpMetric(cargo) ||
    isNonEmptyOpMetric(idle)
  );
}

function buildDifferenceView(difference: number | null): ConsumptionDifferenceView {
  return {
    differenceText: formatSignedDifference(difference),
    differenceClass: getDifferenceClass(difference),
    performanceLabel: getPerformanceLabel(difference),
  };
}

const ROW_MEMO_CLEAR_FNS: Array<(element: object) => void> = [];

/**
 * Invalidate all cached cell views/tooltips for a specific row object
 * (e.g., when the user edits optimization consumption in-place).
 */
export function invalidateRowOptimizationCache(element: any): void {
  if (!element || typeof element !== 'object') {
    return;
  }
  for (const clearFn of ROW_MEMO_CLEAR_FNS) {
    clearFn(element);
  }
}

/**
 * The database table evaluates each cell view/tooltip several times per row on
 * every change-detection pass (interpolation, class binding, `*ngIf`, tooltip).
 * These are pure functions of `(element, op)` and rows are replaced wholesale on
 * reload rather than mutated, so results can be cached against the row object.
 *
 * A `WeakMap` keeps this leak-free: entries die with the row they belong to.
 * Returning the identical object reference also stops Angular re-running the
 * bindings that consume it.
 */
function memoizePerRow<K extends string, T>(
  compute: (element: any, op: K) => T,
): (element: any, op: K) => T {
  const cache = new WeakMap<object, Map<K, T>>();
  ROW_MEMO_CLEAR_FNS.push((el: object) => {
    cache.delete(el);
  });

  return (element: any, op: K): T => {
    if (!element || typeof element !== 'object') {
      return compute(element, op);
    }
    let byOp = cache.get(element);
    if (!byOp) {
      byOp = new Map<K, T>();
      cache.set(element, byOp);
    }
    if (byOp.has(op)) {
      return byOp.get(op) as T;
      }
    const result = compute(element, op);
    byOp.set(op, result);
    return result;
  };
}

export const buildCpWarrantedCellView = memoizePerRow(
  (element: any, op: PortDatabaseOpKey): ConsumptionDifferenceView => {
    const actual = getOpActualValue(element, op);
    const warranted = getOpWarrantedValue(element, op);
    return buildDifferenceView(calculateConsumptionDifference(actual, warranted));
  },
);

export const buildDesignCellView = memoizePerRow(
  (element: any, op: PortDatabaseOpKey): ConsumptionDifferenceView => {
    const actual = getOpActualValue(element, op);
    const design = getOpDesignValue(element, op);
    return buildDifferenceView(calculateConsumptionDifference(actual, design));
  },
);

export function buildDesignOverallCellView(
  actualTotal: number | null,
  designOverall: number | null,
): ConsumptionDifferenceView {
  return buildDifferenceView(calculateConsumptionDifference(actualTotal, designOverall));
}

export const buildCpWarrantedTooltip = memoizePerRow(
  (element: any, op: PortDatabaseOpKey): string => {
    const actual = getOpActualValue(element, op);
    const warranted = getOpWarrantedValue(element, op);
    const difference = calculateConsumptionDifference(actual, warranted);

    return [
      `Operation: ${OP_LABELS[op]}`,
      `Actual Consumption: ${formatTooltipMetric(actual)}`,
      `C/P Warranted Consumption: ${formatTooltipMetric(warranted)}`,
      `Difference: ${formatSignedDifference(difference)}`,
      `Performance: ${getPerformanceLabel(difference)}`,
    ].join('\n');
  },
);

export const buildDesignTooltip = memoizePerRow(
  (element: any, op: PortDatabaseOpKey): string => {
    const actual = getOpActualValue(element, op);
    const design = getOpDesignValue(element, op);
    const difference = calculateConsumptionDifference(actual, design);

    return [
      `Operation: ${OP_LABELS[op]}`,
      `Actual Consumption: ${formatTooltipMetric(actual)}`,
      `Design Consumption: ${formatTooltipMetric(design)}`,
      `Difference: ${formatSignedDifference(difference)}`,
      `Performance: ${getPerformanceLabel(difference)}`,
    ].join('\n');
  },
);

export function buildDesignOverallTooltip(
  actualTotal: number | null,
  designOverall: number | null,
): string {
  const difference = calculateConsumptionDifference(actualTotal, designOverall);

  return [
    `Actual Total Consumption: ${formatTooltipMetric(actualTotal)}`,
    `Design Overall Consumption: ${formatTooltipMetric(designOverall)}`,
    `Difference: ${formatSignedDifference(difference)}`,
    `Performance: ${getPerformanceLabel(difference)}`,
  ].join('\n');
}

/**
 * Resolves Optimization Consumption for a given operation leg.
 * Uses the exact optimization fields from /pc-vesselView:
 * 1. element.bunker (if populated, e.g. optimization_man, optimization_cargo_ops, optimization_idle)
 * 2. element.optimization_consumption_mt.{manoeuvring|cargo_ops|idle}
 * 3. Top-level optimization aliases (optimization_consumption_manoeuvring_mt, etc.)
 *
 * NOTE: Strictly DOES NOT fall back to Design Consumption fields!
 */
export type OptimizationLegKey =
  | 'optimization_man'
  | 'optimization_cargo_ops'
  | 'optimization_idle'
  | PortDatabaseOpKey;

export const getOptimizationLegConsumption = memoizePerRow(
  (element: any, keyOrOp: OptimizationLegKey): number | null =>
    computeOptimizationLegConsumption(element, keyOrOp),
);

function computeOptimizationLegConsumption(
  element: any,
  keyOrOp: OptimizationLegKey,
): number | null {
  if (!element) {
    return null;
  }

  const key: 'optimization_man' | 'optimization_cargo_ops' | 'optimization_idle' =
    keyOrOp === 'maneuvering' || keyOrOp === 'optimization_man'
      ? 'optimization_man'
      : keyOrOp === 'cargoOps' || keyOrOp === 'optimization_cargo_ops'
        ? 'optimization_cargo_ops'
        : 'optimization_idle';

  const bunkerVal = element?.bunker?.[key];
  if (bunkerVal != null && bunkerVal !== '') {
    const parsed = Number(bunkerVal);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  const optMt = element?.optimization_consumption_mt;
  if (optMt && typeof optMt === 'object') {
    const raw =
      key === 'optimization_man'
        ? (optMt.manoeuvring ?? optMt.maneuvering ?? optMt.man)
        : key === 'optimization_cargo_ops'
          ? optMt.cargo_ops
          : optMt.idle;
    if (raw != null && raw !== '') {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  const topLevelAliases: Record<string, string[]> = {
    optimization_man: [
      'optimization_man',
      'optimization_maneuvering',
      'optimization_manoeuvring',
      'optimization_consumption_manoeuvring_mt',
    ],
    optimization_cargo_ops: [
      'optimization_cargo_ops',
      'optimization_consumption_cargo_ops_mt',
    ],
    optimization_idle: [
      'optimization_idle',
      'optimization_consumption_idle_mt',
    ],
  };

  for (const alias of topLevelAliases[key] || []) {
    if (element?.[alias] != null && element[alias] !== '') {
      const parsed = Number(element[alias]);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return null;
}

export function getOpOptimizationValue(
  element: any,
  op: PortDatabaseOpKey,
): number | null {
  return getOptimizationLegConsumption(element, op);
}

export const buildOptimizationCellView = memoizePerRow(
  (element: any, op: PortDatabaseOpKey): ConsumptionDifferenceView => {
    const actual = getOpActualValue(element, op);
    const optimization = getOpOptimizationValue(element, op);
    return buildDifferenceView(
      calculateConsumptionDifference(actual, optimization),
    );
  },
);

export const buildOptimizationTooltip = memoizePerRow(
  (element: any, op: PortDatabaseOpKey): string => {
    const actual = getOpActualValue(element, op);
    const optimization = getOpOptimizationValue(element, op);
    if (optimization == null) {
      return [
        `Operation: ${OP_LABELS[op]}`,
        `Actual Consumption: ${formatTooltipMetric(actual)}`,
        `Optimization Consumption: --`,
      ].join('\n');
    }
    const difference = calculateConsumptionDifference(actual, optimization);

    return [
      `Operation: ${OP_LABELS[op]}`,
      `Actual Consumption: ${formatTooltipMetric(actual)}`,
      `Optimization Consumption: ${formatTooltipMetric(optimization)}`,
      `Difference: ${formatSignedDifference(difference)}`,
      `Performance: ${getPerformanceLabel(difference)}`,
    ].join('\n');
  },
);

